import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import MessageBubble from "../components/MessageBubble";
import BlockRenderer from "../components/blocks/BlockRenderer";
import UserMenuButton from "../components/header/UserMenuButton";
import FavoritesButton from "../components/header/FavoritesButton";
import MainMenuButton from "../components/header/MainMenuButton";
import TypingRichText from "../components/TypingRichText";
import logo from "../assets/konteyner_logo.png";
import "../styles/ChatLayout.css";

export default function ChatLayout({
  chatId,
  messages,
  onBlockAction,
  onMessageAction,
  onOpenChat,
  onNewChat,
  onLogoClick,
  overlayOpen = false,
  footer,
  favoriteItems = [],
  onOpenProduct,
  onRemoveFavorite,
}) {
  const bottomRef = useRef(null);
  const chatRef = useRef(null);
  const location = useLocation();

  const [openMenu, setOpenMenu] = useState(null);
  const [typedDoneMap, setTypedDoneMap] = useState({});

  function scrollToBottom(behavior = "auto") {
    const el = chatRef.current;
    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior,
    });
  }

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      scrollToBottom("auto");
    });

    const t = setTimeout(() => {
      scrollToBottom("auto");
    }, 120);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [chatId, messages.length]);

  useEffect(() => {
    if (overlayOpen) return;

    const t = setTimeout(() => {
      scrollToBottom("auto");
    }, 80);

    return () => clearTimeout(t);
  }, [overlayOpen]);

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      if (distanceFromBottom < 120) {
        scrollToBottom("auto");
      }
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setTypedDoneMap({});
  }, [chatId]);

  useEffect(() => {
    function handleOpenUserMenu() {
      setOpenMenu("user");
    }

    window.addEventListener("chatshop:open-user-menu", handleOpenUserMenu);

    return () => {
      window.removeEventListener(
        "chatshop:open-user-menu",
        handleOpenUserMenu
      );
    };
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpenMenu(null);
    }

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
  if (location.state?.reopenUserMenu) {
    setOpenMenu("user");
    return;
  }

  if (location.state?.reopenMainMenu) {
    setOpenMenu("main");
  }
}, [location.key, location.state]);

  const safeMessages = messages || [];
  const hasUserMessage = safeMessages.some((m) => m?.role === "user");

  const isWelcome =
    !hasUserMessage &&
    safeMessages.length === 1 &&
    safeMessages[0]?.role === "assistant" &&
    !safeMessages[0]?.typing;

  return (
    <div className="page">
      <header className="topbar">
        <div className="topbarLeft">
          <UserMenuButton
            chatId={chatId}
            open={openMenu === "user"}
            onToggle={() =>
              setOpenMenu((v) => (v === "user" ? null : "user"))
            }
            onClose={() => setOpenMenu(null)}
            onOpenChat={onOpenChat}
            onNewChat={onNewChat}
          />
        </div>

        <div className="topbarCenter" aria-label="Лого">
          <img
            src={logo}
            alt="ChatShop"
            className="brandLogoImg"
            onClick={onLogoClick}
          />
        </div>

        <div className="topbarRight">
          <FavoritesButton
            open={openMenu === "favorites"}
            isActive={favoriteItems.length > 0}
            onToggle={() =>
              setOpenMenu((v) => (v === "favorites" ? null : "favorites"))
            }
            onClose={() => setOpenMenu(null)}
            items={favoriteItems}
            onOpenProduct={onOpenProduct}
            onRemoveItem={onRemoveFavorite}
            topOffset={60}
          />

          <MainMenuButton
            chatId={chatId}
            open={openMenu === "main"}
            onToggle={() =>
              setOpenMenu((v) => (v === "main" ? null : "main"))
            }
            onClose={() => setOpenMenu(null)}
          />
        </div>
      </header>

      <main
        ref={chatRef}
        className={`chat ${isWelcome ? "chat--welcome" : ""}`}
      >
        {isWelcome ? (
          <div className="welcomeWrap">
            <div className="welcomeText">
              <TypingRichText
                key={chatId}
                className="bubbleText"
                text={safeMessages[0]?.text}
                parts={safeMessages[0]?.parts}
                speed={14}
                enabled={true}
              />
            </div>
          </div>
        ) : (
          safeMessages.map((m, idx) => (
            <Fragment key={idx}>
              <MessageBubble
                role={m.role}
                text={m.text}
                parts={m.parts}
                meta={m.meta}
                typing={!!m.typing}
                animate={!!m.animate}
                onAction={onMessageAction}
                onTypingDone={() => {
                  setTypedDoneMap((prev) => {
                    if (prev[idx]) return prev;
                    return { ...prev, [idx]: true };
                  });
                }}
              />

              {Array.isArray(m.blocks) &&
                m.blocks.length > 0 &&
                (!m.animate || typedDoneMap[idx]) && (
                  <div className="blocks">
                    {m.blocks.map((b, i) => (
                      <BlockRenderer
                        key={`${idx}_${i}`}
                        block={b}
                        onAction={onBlockAction}
                      />
                    ))}
                  </div>
                )}
            </Fragment>
          ))
        )}

        <div ref={bottomRef} />
      </main>

      <div className="footerSlot">{footer}</div>
    </div>
  );
} 